import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Brain, ArrowRight, Mail, Phone } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import PasswordInput from "../components/PasswordInput";
import { toast } from "sonner";

// iter130 Phase A — Forgot Password wizard.
//
// Two paths:
//   * "email" — request a Resend reset link (30 min TTL, single-use)
//   * "phone" — MSG91 OTP (10 min TTL). Backend returns 501 until
//     the MSG91 feature flag is on (blocked on TRAI DLT).
//
// After a successful reset the backend bumps `token_version` which
// invalidates every existing session for that user. We surface that
// to the user with a "signed out of all devices" toast.
export default function ForgotPassword() {
  const { requestPasswordReset, confirmPasswordReset } = useAuth();
  const nav = useNavigate();
  // iter130 Phase A follow-up: prefill the email from the ?email= query
  // param the Login page passes when the user clicks "Forgot?" — avoids
  // the "typed a different email than my account" pitfall.
  const [searchParams] = useSearchParams();
  const initialEmail = searchParams.get("email") || "";

  const [step, setStep] = useState("choose");    // choose | verify | done
  const [method, setMethod] = useState("email");
  const [email, setEmail] = useState(initialEmail);
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onRequest = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const payload = method === "email"
      ? { method, email: email.trim().toLowerCase() }
      : { method, phone: phone.trim() };
    const res = await requestPasswordReset(payload);
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error || "Something went wrong. Try again.");
      return;
    }
    if (method === "email") {
      // For email flow the user leaves this page and returns via the
      // link they receive. Show a "check inbox" screen here.
      setStep("done");
    } else {
      // Phone flow — proceed to OTP + new-password step.
      setStep("verify");
    }
  };

  const onConfirmPhone = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setSubmitting(true);
    const res = await confirmPasswordReset({
      email: email.trim().toLowerCase(),
      otp: otp.trim(),
      newPassword,
    });
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error || "Invalid OTP or expired code.");
      return;
    }
    toast.success("Password updated. Signed out of all devices — please sign in with your new password.");
    nav("/login");
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[var(--bt-bg)]">
      <div className="w-full max-w-md space-y-6 bg-[var(--bt-surface)] border border-[var(--bt-border)] rounded-sm p-8">
        <div className="flex items-center gap-3 mb-1">
          <span className="w-8 h-8 rounded-sm bg-[var(--bt-accent)] flex items-center justify-center">
            <Brain className="w-4 h-4 text-black" strokeWidth={2.5} />
          </span>
          <span className="font-heading text-lg">Brain<span className="text-[var(--bt-accent)]">TRADES</span></span>
        </div>

        {step === "choose" && (
          <form onSubmit={onRequest} data-testid="forgot-form-choose" className="space-y-4">
            <div>
              <p className="bt-label">Reset password</p>
              <h1 className="font-heading text-2xl mt-1">How should we reach you?</h1>
              <p className="text-sm text-[var(--bt-text-muted)] mt-1">
                Pick the channel we should send your reset code / link to.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-2" role="tablist">
              <button
                type="button"
                onClick={() => setMethod("email")}
                data-testid="forgot-tab-email"
                className={`h-10 rounded-sm text-sm border ${method === "email"
                  ? "border-[var(--bt-accent)] bg-[var(--bt-accent)]/10 text-[var(--bt-text)]"
                  : "border-[var(--bt-border)] text-[var(--bt-text-muted)]"}`}
              >
                <Mail className="w-4 h-4 inline mr-2" /> Email
              </button>
              <button
                type="button"
                onClick={() => setMethod("phone")}
                data-testid="forgot-tab-phone"
                className={`h-10 rounded-sm text-sm border ${method === "phone"
                  ? "border-[var(--bt-accent)] bg-[var(--bt-accent)]/10 text-[var(--bt-text)]"
                  : "border-[var(--bt-border)] text-[var(--bt-text-muted)]"}`}
              >
                <Phone className="w-4 h-4 inline mr-2" /> Phone (OTP)
              </button>
            </div>

            {method === "email" ? (
              <div className="space-y-2">
                <Label htmlFor="fp-email" className="bt-label">Registered email</Label>
                <Input
                  id="fp-email" type="email" required autoComplete="email"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="forgot-email-input"
                  className="bg-transparent border-[var(--bt-border)] focus-visible:border-[var(--bt-accent)] focus-visible:ring-1 focus-visible:ring-[var(--bt-accent)] h-11"
                />
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="fp-phone" className="bt-label">Registered phone</Label>
                <Input
                  id="fp-phone" type="tel" required autoComplete="tel"
                  value={phone} onChange={(e) => setPhone(e.target.value)}
                  placeholder="+91 98XXXXXXXX"
                  data-testid="forgot-phone-input"
                  className="bg-transparent border-[var(--bt-border)] focus-visible:border-[var(--bt-accent)] focus-visible:ring-1 focus-visible:ring-[var(--bt-accent)] h-11"
                />
                <p className="text-[11px] text-[var(--bt-text-muted)]">
                  Phone reset requires an OTP — we&apos;ll SMS you a 6-digit code.
                </p>
              </div>
            )}

            {error && (
              <div data-testid="forgot-error" className="text-sm text-[var(--bt-danger)] border border-[var(--bt-danger)]/40 bg-[var(--bt-danger)]/10 px-3 py-2 rounded-sm">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              data-testid="forgot-submit-btn"
              className="w-full h-11 rounded-sm bg-[var(--bt-accent)] hover:bg-[var(--bt-accent-hover)] text-black font-bold"
            >
              {submitting
                ? "Sending…"
                : (method === "email" ? "Email me a reset link" : "Send OTP")}
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>

            <p className="text-sm text-center text-[var(--bt-text-muted)]">
              Remembered it?{" "}
              <Link to="/login" className="text-[var(--bt-accent)] hover:underline">
                Back to sign in
              </Link>
            </p>
          </form>
        )}

        {step === "verify" && (
          <form onSubmit={onConfirmPhone} data-testid="forgot-form-verify" className="space-y-4">
            <div>
              <p className="bt-label">Verify OTP</p>
              <h1 className="font-heading text-2xl mt-1">Enter the 6-digit code</h1>
              <p className="text-sm text-[var(--bt-text-muted)] mt-1">
                Sent to {phone}. Code expires in 10 minutes.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="fp-email-confirm" className="bt-label">Confirm your email</Label>
              <Input
                id="fp-email-confirm" type="email" required
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="forgot-verify-email-input"
                className="bg-transparent border-[var(--bt-border)] focus-visible:border-[var(--bt-accent)] focus-visible:ring-1 focus-visible:ring-[var(--bt-accent)] h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fp-otp" className="bt-label">OTP</Label>
              <Input
                id="fp-otp" inputMode="numeric" pattern="[0-9]*" required
                value={otp} onChange={(e) => setOtp(e.target.value)}
                placeholder="123456" maxLength={8}
                data-testid="forgot-otp-input"
                className="bg-transparent border-[var(--bt-border)] focus-visible:border-[var(--bt-accent)] focus-visible:ring-1 focus-visible:ring-[var(--bt-accent)] h-11 tracking-widest font-mono"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fp-new-pw" className="bt-label">New password (min 8 chars)</Label>
              <PasswordInput
                id="fp-new-pw"
                required autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                testId="forgot-new-password-input"
              />
            </div>
            {error && (
              <div data-testid="forgot-error" className="text-sm text-[var(--bt-danger)] border border-[var(--bt-danger)]/40 bg-[var(--bt-danger)]/10 px-3 py-2 rounded-sm">
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={submitting}
              data-testid="forgot-confirm-btn"
              className="w-full h-11 rounded-sm bg-[var(--bt-accent)] hover:bg-[var(--bt-accent-hover)] text-black font-bold"
            >
              {submitting ? "Updating…" : "Reset password"}
            </Button>
          </form>
        )}

        {step === "done" && (
          <div className="space-y-4" data-testid="forgot-form-done">
            <div>
              <p className="bt-label">Check your inbox</p>
              <h1 className="font-heading text-2xl mt-1">Reset link sent</h1>
              <p className="text-sm text-[var(--bt-text-muted)] mt-2 leading-relaxed">
                If <span className="font-mono">{email}</span> is registered, we&apos;ve emailed you a reset link. It expires in 30 minutes and can only be used once.
              </p>
              <p className="text-xs text-[var(--bt-text-muted)] mt-3">
                Didn&apos;t get it? Check your spam folder or wait a minute and{" "}
                <button
                  type="button"
                  onClick={() => setStep("choose")}
                  className="underline text-[var(--bt-accent)]"
                  data-testid="forgot-retry"
                >
                  try again
                </button>.
              </p>
            </div>
            <Link
              to="/login"
              className="inline-flex items-center gap-1 text-sm text-[var(--bt-text-muted)] hover:text-[var(--bt-accent)]"
            >
              ← Back to sign in
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
